// useOrderContext — fetches Shopify customer + up to 5 recent orders for the
// conversation currently open in Command Center. Powers the right-rail sidebar.
//
// Contract mirrors packages/backend/src/routes/context.ts. We can't value-import
// from the backend (monorepo boundary + @md/shared runtime gotcha), so the
// response types are re-declared here. Keep them in sync; CI will break if
// they drift because the sidebar will stop rendering fields.
//
// Freshness:
//   staleTime = 2.5 min (half of backend cache TTL). This guarantees the UI
//   never out-paces the server cache: if we requested sooner than 2.5 min,
//   we'd still hit the server-side cache and get an identical payload, just
//   wasting a round trip. At 2.5 min we refetch roughly when the cache is
//   about to refresh anyway.
//
// retry = 0: these 502s are expected when Shopify credentials aren't seeded
// yet (pre-Phase 5b deploy). No reason to hammer the backend; show the empty
// state and move on.
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';

/**
 * Phase 8: ShipStation integration. `source` is the raw discriminator
 * ('md' | 'brokerage' | 'shipstation'); `sourceLabel` + `sourceChipColor`
 * carry the chip display values ("ETSY MD", "AMAZON", "BROKERAGE", etc.)
 * that the Customer Panel renders per-order.
 */
export type OrderSource = 'md' | 'brokerage' | 'shipstation';
export type OrderChipColor = 'clay' | 'sky' | 'leather' | 'monstera' | 'stone';

export interface OrderContextCustomer {
  shopifyCustomerId: string;
  displayName: string | null;
  /** First name from Shopify — used by the drafter + panel greeting. */
  firstName?: string | null;
  /** Last name from Shopify. */
  lastName?: string | null;
  email: string | null;
  numberOfOrders: number;
  lifetimeSpend: {
    amount: string;
    currencyCode: string;
  };
  /**
   * Formatted "City, ST" / "City, Country" string from the Shopify
   * defaultAddress. Optional on the wire because cached rows written
   * before this field was added don't carry it.
   */
  location?: string | null;
  /**
   * Which Shopify shop(s) matched. Narrowed to the Shopify sources only
   * because ShipStation doesn't contribute to customer stats.
   */
  sourceShops?: Array<'md' | 'brokerage'>;
}

export interface OrderContextLineItem {
  title: string;
  quantity: number;
  sku: string | null;
}

export interface OrderContextTracking {
  number: string | null;
  url: string | null;
  company: string | null;
}

export interface OrderContextFulfillment {
  status: string;
  createdAt: string;
  estimatedDeliveryAt: string | null;
  tracking: OrderContextTracking[];
}

export interface OrderContextOrder {
  shopifyOrderId: string;
  name: string;
  createdAt: string;
  fulfillmentStatus: string;
  financialStatus: string | null;
  total: {
    amount: string;
    currencyCode: string;
  };
  lineItems: OrderContextLineItem[];
  fulfillments: OrderContextFulfillment[];
  /**
   * Which store this order originated from. Optional on the wire because
   * cached rows written before Phase 5b.2 don't have it — the sidebar
   * treats missing as 'md'.
   */
  source?: OrderSource;
  /**
   * Human-facing chip text rendered by the Customer Panel. When undefined
   * (older cache row) the panel infers from `source` — 'md' → 'SHOPIFY MD',
   * 'brokerage' → 'BROKERAGE', 'shipstation' → 'SHIPSTATION'.
   */
  sourceLabel?: string;
  /**
   * Chip color suffix. Maps 1:1 to a CSS class on .order-chip. Optional on
   * the wire — sidebar falls back to 'clay' when missing.
   */
  sourceChipColor?: OrderChipColor;
  /**
   * Deep-link to the originating system's order page (polish #26). Populated
   * for Shopify MD/Brokerage (admin URL) and Etsy MD (seller "Your Orders"
   * receipt URL). Null or undefined for Etsy HK, Amazon, MKT BOX, and
   * older cached rows written before the field existed — Customer Panel
   * just skips the icon in those cases.
   */
  adminUrl?: string | null;
}

export interface OrderContextPayload {
  customer: OrderContextCustomer | null;
  orders: OrderContextOrder[];
  cached: boolean;
  cachedAt: string;
}

const HALF_OF_BACKEND_TTL_MS = 2.5 * 60 * 1000;

export function useOrderContext(conversationId: string | undefined) {
  return useQuery<OrderContextPayload>({
    queryKey: ['orderContext', conversationId],
    queryFn: async () => {
      if (!conversationId) {
        throw new Error('conversationId required');
      }
      return apiGet<OrderContextPayload>(`/api/context/${conversationId}/order`);
    },
    enabled: Boolean(conversationId),
    staleTime: HALF_OF_BACKEND_TTL_MS,
    retry: 0,
  });
}
